import { Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { SharedStorageService } from 'common/util/module'
import { RestoreEndpointRequest, TapeFile } from 'elements-sdk'
import { IdentityService } from 'main/services/identity.service'
import { TapeJobsService } from './tapeJobs.service'
import { NotifyService } from 'common/util/services/notify.service'

@Injectable({ providedIn: 'root' })
export class TapeRestorerService {
  private queue: TapeFile[]
  private excludeList: string[]
  private QUEUE_KEY = 'tape:restore-queue'
  private EXCLUDE_KEY = 'tape:restore-exclude-list'

  constructor (
    private api: APIService,
    private sharedStorage: SharedStorageService,
    private translate: TranslateService,
    private tapeJobs: TapeJobsService,
    identity: IdentityService,
    private notify: NotifyService,
  ) {
    if (!identity.loggedIn) {
      return
    }

    this.queue = sharedStorage.get(this.QUEUE_KEY, [])
    this.excludeList = sharedStorage.get(this.EXCLUDE_KEY, [])
  }

  clear (): void {
    this.queue = []
    this.excludeList = []
    this.saveQueue()
    this.saveExcludeList()
  }

  private saveQueue () {
    this.sharedStorage.querySet(this.QUEUE_KEY, this.queue)
  }

  private saveExcludeList () {
    this.sharedStorage.querySet(this.EXCLUDE_KEY, this.excludeList)
  }

  enqueue (items: TapeFile[], notifyUser = false): void {
    for (const item of items) {
      if (this.getExcludeList().includes(item.path)) {
        continue
      }
      if (!this.getQueue().some(x => x.id === item.id)) {
        this.queue = [...this.queue, item]
      }
    }
    this.saveQueue()
    if (notifyUser) {
      this.notify.custom(
        'info',
        this.translate.instant('Restore'),
        this.translate.instant('Files added. Click to begin restore.'),
        { url: '/archive/restore' },
      )
    }
  }

  getQueue (): TapeFile[] {
    return this.queue
  }

  getExcludeList (): string[] {
    return this.excludeList
  }

  exclude (items: TapeFile[], notifyUser = false): void {
    for (const item of items) {
      this.excludePath(item.path)
    }
    if (notifyUser) {
      this.notify.custom(
        'info',
        this.translate.instant('Restore'),
        this.translate.instant('Files excluded. Click to begin restore.'),
        { url: '/archive/restore' },
      )
    }
  }

  excludePath (path: string): void {
    if (!this.excludeList.includes(path)) {
      this.queue = this.queue.filter(x => x.path !== path)
      this.excludeList = [...this.excludeList, path]
    }
    this.saveQueue()
    this.saveExcludeList()
  }

  async start (options: Partial<RestoreEndpointRequest>): Promise<void> {
    const tapes = [...new Set(this.queue.map(x => x.tape.name))]

    for (const tape of tapes) {
      await this.api.tapeArchive.restoreFromTape({
        restoreEndpointRequest: {
          name: 'Restore',
          ...options,
          source: this.queue.filter(x => x.tape.name === tape),
          exclude: this.excludeList,
          startDate: new Date(),
          tape,
        },
      })
    }

    this.tapeJobs.refresh()
  }
}
